import { useJobStore } from '@core/job/useJobStore';
import { useWorkerStore } from '@core/worker/useWorkerStore';
import { useBlockedUserStore } from '@core/blockedUser/useBlockedUserStore';
import { BlockedUserOwnerType, UserRole } from '@core/generated/gql-types';
import {
  TemplateTerm,
  useTemplateStore,
} from '@core/template/useTemplateStore';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Skeleton } from '@/components/shared/Skeleton';
import Button from '@/components/Button';

interface ClientWorkersProps {
  clientId?: string;
}

const ClientWorkers: React.FC<ClientWorkersProps> = ({ clientId }) => {
  const [hasFetched, setHasFetched] = useState(false);
  const [pendingWorkerId, setPendingWorkerId] = useState<string | null>(null);
  const navigate = useNavigate();
  const { jobs, fetchAllJobsForClient } = useJobStore();
  const {
    isLoading: isWorkersLoading,
    getWorkersWorkedForClient,
    fetchWorkersForClient,
  } = useWorkerStore();
  const {
    fetchBlockedUsersForOwner,
    blockUser,
    unblockUser,
    isUserBlockedForOwner,
  } = useBlockedUserStore();
  const workers = getWorkersWorkedForClient(clientId || '', jobs);
  const { getTerm } = useTemplateStore();

  useEffect(() => {
    if (clientId && !isWorkersLoading && !hasFetched) {
      setHasFetched(true);
      fetchWorkersForClient(clientId);
      fetchAllJobsForClient(clientId);
      fetchBlockedUsersForOwner(
        clientId,
        BlockedUserOwnerType.Client,
        UserRole.Worker,
      );
    }
  }, [
    clientId,
    isWorkersLoading,
    hasFetched,
    fetchBlockedUsersForOwner,
    fetchWorkersForClient,
    fetchAllJobsForClient,
  ]);

  const getStatusBadgeClass = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'blocked':
        return 'bg-gray-200 text-gray-800';
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleWorkerClick = (workerId: string) => {
    navigate(`/workers/${workerId}`);
  };

  const handleToggleBlock = async (workerId: string) => {
    if (!clientId || pendingWorkerId) return;

    setPendingWorkerId(workerId);
    try {
      if (isUserBlockedForOwner(clientId, workerId)) {
        await unblockUser(clientId, BlockedUserOwnerType.Client, workerId);
      } else {
        await blockUser(clientId, BlockedUserOwnerType.Client, workerId);
      }
    } catch (error) {
      console.error('Error toggling worker block state:', error);
    } finally {
      setPendingWorkerId(null);
    }
  };

  return (
    <div className="rounded-lg bg-white p-6 shadow-sm">
      <h2 className="mb-4 text-xl font-semibold">Candidates</h2>
      <div className="space-y-4">
        <p className="text-gray-600">
          Manage candidates for {getTerm(TemplateTerm.CLIENT, false)} users
          here.
        </p>

        <div className="flex-1 overflow-auto rounded-lg border border-gray-200 bg-white p-2 shadow-sm">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold tracking-wider text-gray-600 uppercase">
                  Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold tracking-wider text-gray-600 uppercase">
                  Email
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold tracking-wider text-gray-600 uppercase">
                  Status
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold tracking-wider text-gray-600 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {isWorkersLoading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <tr key={index}>
                    <td className="border-t border-gray-200 px-4 py-3 align-middle">
                      <Skeleton height="h-4" width="w-32" />
                    </td>
                    <td className="border-t border-gray-200 px-4 py-3 align-middle">
                      <Skeleton height="h-4" width="w-40" />
                    </td>
                    <td className="border-t border-gray-200 px-4 py-3 align-middle">
                      <Skeleton height="h-6" width="w-16" rounded-sm="full" />
                    </td>
                    <td className="border-t border-gray-200 px-4 py-3 text-right align-middle">
                      <Skeleton height="h-8" width="w-20" />
                    </td>
                  </tr>
                ))
              ) : workers.length === 0 ? (
                <tr>
                  <td
                    colSpan={4}
                    className="border-t border-gray-200 px-4 py-6 text-center text-gray-500"
                  >
                    No workers found
                  </td>
                </tr>
              ) : (
                workers.map((worker) => {
                  const isBlocked = clientId
                    ? isUserBlockedForOwner(clientId, worker.id)
                    : false;
                  const isPending = pendingWorkerId === worker.id;
                  const buttonLabel = isBlocked ? 'Unblock' : 'Block';
                  const rowClasses = isBlocked
                    ? 'bg-gray-50 hover:bg-gray-100'
                    : 'hover:bg-gray-100';
                  const blockedCellOutline = isBlocked
                    ? '!border-y-2 !border-y-gray-400'
                    : '';
                  const blockedFirstCellOutline = isBlocked
                    ? '!border-l-2 !border-l-gray-400'
                    : '';
                  const blockedLastCellOutline = isBlocked
                    ? '!border-r-2 !border-r-gray-400'
                    : '';
                  const mutedTextClass = isBlocked ? 'text-gray-500' : '';

                  return (
                    <tr
                      key={worker.id}
                      onClick={() => handleWorkerClick(worker.id)}
                      className={`cursor-pointer transition-colors ${rowClasses}`}
                    >
                      <td
                        className={`border-t border-gray-200 px-4 py-3 align-middle ${mutedTextClass} ${blockedCellOutline} ${blockedFirstCellOutline}`}
                      >
                        {`${worker.firstName || ''} ${worker.lastName || ''}`.trim() ||
                          'Unknown'}
                      </td>
                      <td
                        className={`border-t border-gray-200 px-4 py-3 align-middle ${mutedTextClass} ${blockedCellOutline}`}
                      >
                        {worker.email || 'No email'}
                      </td>
                      <td
                        className={`border-t border-gray-200 px-4 py-3 align-middle ${blockedCellOutline}`}
                      >
                        <span
                          className={`rounded-full px-2 py-1 text-xs font-medium ${getStatusBadgeClass(isBlocked ? 'blocked' : worker.status)}`}
                        >
                          {isBlocked ? 'BLOCKED' : worker.status || 'Unknown'}
                        </span>
                      </td>
                      <td
                        onClick={(event) => event.stopPropagation()}
                        className={`border-t border-gray-200 px-4 py-3 text-right align-middle ${blockedCellOutline} ${blockedLastCellOutline}`}
                      >
                        <Button
                          variant="outline"
                          onClick={() => handleToggleBlock(worker.id)}
                          disabled={isPending || !clientId}
                          className="ml-auto !w-24 !border-red-600 !bg-transparent !px-3 !py-1.5 !text-sm !text-red-600 hover:!bg-red-50"
                        >
                          {isPending ? (
                            <span
                              aria-label={`${buttonLabel} in progress`}
                              className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-red-600 border-t-transparent"
                            />
                          ) : (
                            buttonLabel
                          )}
                        </Button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ClientWorkers;
